import {
  CategoryScale,
  Chart as Chartjs,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import classNames from 'classnames/bind';
import { IFare as IPriceGraphFare } from 'hooks/usePriceGraph';
import { IFare as IPriceGraphDetailFare } from 'hooks/usePriceGraphDetail';
import findLastIndex from 'lodash/findLastIndex';
import { useEffect, useRef } from 'react';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

Chartjs.register(
  CategoryScale,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
);

interface IProps {
  areaCode: string;
  fares: IPriceGraphFare[] | IPriceGraphDetailFare[];
  period: number;
}

export default function Chart({ areaCode, fares, period }: IProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chartjs<'line', number[], string>>();
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const labels = Array.from({ length: period }, (_, i) => `${period - i}`);
    const datasets = [
      {
        data: fares.map(({ lowestFare }) => lowestFare),
        spanGaps: true,
        borderColor: '#5399F5',
        borderWidth: 1.5,
        pointBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointBorderWidth: 0,
        pointHoverBackgroundColor: '#5399F5',
        pointHoverBorderWidth: 0,
        pointHoverRadius: 3.5,
      },
    ];
    if (!chartRef.current) {
      chartRef.current = new Chartjs(canvasRef.current, {
        type: 'line',
        data: { datasets, labels },
        options: {
          interaction: { intersect: false },
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              enabled: false,
              external({ chart, tooltip }) {
                if (!tooltipRef.current) return;
                const {
                  body: [
                    {
                      lines: [line],
                    },
                  ],
                  opacity,
                  title: [title],
                } = tooltip;
                if (!opacity) {
                  tooltipRef.current.style.opacity = '0';
                  return;
                }
                const {
                  canvas: { offsetLeft: positionX, offsetTop: positionY },
                } = chart;
                tooltipRef.current.children[0].innerHTML = `${title}일 전<br />${line}원`;
                tooltipRef.current.style.opacity = '1';
                tooltipRef.current.style.left = `${
                  positionX + tooltip.caretX
                }px`;
                tooltipRef.current.style.top = `${
                  positionY + tooltip.caretY
                }px`;
              },
            },
          },
          scales: {
            x: {
              grid: { tickLength: 0 },
              offset: true,
              ticks: {
                color: '#999999',
                padding: 10,
                callback(index) {
                  const days = +this.getLabelForValue(index as number);
                  return !(days % (period / 6)) ? `${days}일 전` : undefined;
                },
              },
            },
            y: {
              grid: { borderColor: 'rgba(0, 0, 0, 0)', tickLength: 0 },
              ticks: {
                color: '#666666',
                padding: 9,
                callback: value => Math.round((value as number) / 10000),
              },
            },
          },
        },
        plugins: [
          {
            id: 'afterRender',
            afterRender(chart) {
              if (
                !chart.tooltip ||
                chart.getActiveElements().length ||
                chart.tooltip.getActiveElements().length
              ) {
                return;
              }
              const index = findLastIndex(
                chart.data.datasets[0].data,
                value => !!value,
              );
              chart.setActiveElements([{ datasetIndex: 0, index }]);
              chart.tooltip.setActiveElements([{ datasetIndex: 0, index }], {
                x: 0,
                y: 0,
              });
              chart.update();
            },
          },
        ],
      });
    } else {
      chartRef.current.data.labels = labels;
      chartRef.current.data.datasets = datasets;
      chartRef.current.setActiveElements([]);
      chartRef.current.tooltip?.setActiveElements([], { x: 0, y: 0 });
      chartRef.current.update();
    }
  }, [fares, period]);

  useEffect(() => () => chartRef.current?.destroy(), []);

  return (
    <div className={cx('graph')}>
      <canvas
        ref={canvasRef}
        data-event-nlog="click"
        data-event-area-code={`${areaCode}.pricegraphdayprice`}
      />
      <span className={cx('title_y')}>만원</span>
      <div ref={tooltipRef} className={cx('tooltip')} role="tooltip">
        <span className={cx('balloon')} />
      </div>
    </div>
  );
}
